import { forwardRef, useImperativeHandle } from 'react';
import { crearSesionEpayco } from '../services/payloadService';

/**
 * EPaycoButton - Componente invisible para manejar pagos con ePayco Checkout.js
 *
 * Uso:
 *   const ePaycoRef = useRef(null);
 *
 *   await ePaycoRef.current.initiatePayment({
 *     reference: 'REF-123',
 *     name: 'Producto',
 *     description: 'Descripción',
 *     amount: 100000,
 *     email: 'user@example.com',
 *     customerName: 'Juan Pérez',
 *     phone: '3001234567'
 *   });
 */
const EPaycoButton = forwardRef(({ onPaymentSuccess, onPaymentError }, ref) => {
  // Exponer método initiatePayment al componente padre mediante ref
  useImperativeHandle(ref, () => ({
    /**
     * Iniciar proceso de pago con ePayco
     * @param {Object} paymentData - Datos del pago
     */
    async initiatePayment(paymentData) {
      try {
        // Validar que window.ePayco esté disponible
        if (!window.ePayco || !window.ePayco.checkout) {
          throw new Error('ePayco no está cargado. Por favor recarga la página.');
        }

        // 1. Crear sesión en el backend usando payloadService
        const sessionResponse = await crearSesionEpayco({
          reference: paymentData.reference,
          name: paymentData.name,
          description: paymentData.description,
          amount: paymentData.amount,
          email: paymentData.email,
          customerName: paymentData.customerName,
          phone: paymentData.phone,
          extra1: paymentData.extra1 || '',
          extra2: paymentData.extra2 || '',
        });

        if (!sessionResponse.success || !sessionResponse.sessionId) {
          throw new Error('No se pudo crear la sesión de pago');
        }

        const { sessionId } = sessionResponse;


        // Validar datos antes de proceder
        if (!sessionId || typeof sessionId !== 'string') {
          throw new Error('SessionId inválido recibido del backend');
        }

        // 2. Configurar checkout de ePayco
        // IMPORTANTE: Con sesiones, NO usar 'key' - solo sessionId y external
        const handler = window.ePayco.checkout.configure({
          sessionId: sessionId,
          external: false, // false = modal dentro de la página
        });

        // Validar que el handler se creó correctamente
        if (!handler || typeof handler.openNew !== 'function') {
          throw new Error('Error al configurar ePayco checkout handler');
        }

        // Listener para cuando se abre el checkout
        if (typeof handler.on === 'function') {
          handler.on('open', () => {

            // Callback de éxito al abrir
            if (onPaymentSuccess) {
              onPaymentSuccess(sessionId);
            }
          });

          // Listener para errores del checkout
          handler.on('error', (error) => {
            console.error('❌ Error en checkout de ePayco:', error);

            if (onPaymentError) {
              onPaymentError(error);
            }
          });

        }

        // 4. Abrir modal de pago con manejo de errores
        try {
          handler.openNew();
        } catch (openError) {
          console.error('❌ Error al abrir checkout:', openError);
          throw new Error(`Error al abrir modal de pago: ${openError.message}`);
        }

        return { success: true, sessionId };
      } catch (error) {
        console.error('❌ Error al iniciar pago:', error);

        // Callback de error
        if (onPaymentError) {
          onPaymentError(error);
        }

        throw error;
      }
    },
  }));

  // Componente invisible - no renderiza nada
  return null;
});

EPaycoButton.displayName = 'EPaycoButton';

export default EPaycoButton;
